import { Dish, DishModifierGroup, getDishGroupKey, getDishGroupLabel } from '@api/Dishes/Dish';
import { ModifierGroup } from '@api/ModifierGroups/ModifierGroup';
import { OrderLineModifier, OrderLineRemovedIngredient, OrderLineV2 } from '@api/Orders/OrderLine';

// Selecciones del modal de personalización: CLAVE DE RENGLÓN → (optionId → cantidad).
// La clave es `getDishGroupKey` (instanceId del renglón, o groupId en productos
// previos a las instancias) — NO el groupId a secas: un producto puede traer el
// mismo grupo varias veces ("Queso — Hamburguesa 1", "Queso — Hamburguesa 2") y
// cada renglón lleva sus propias selecciones.
export type DishSelections = Map<string, Map<string, number>>;

export const getGroupSelectedTotal = (selections: DishSelections, groupKey: string): number => {
    let total = 0;
    selections.get(groupKey)?.forEach((quantity) => (total += quantity));
    return total;
};

// Cuántas opciones DISTINTAS lleva el renglón (para maxDistinctOptions: "hasta 2 sabores")
export const getGroupDistinctCount = (selections: DishSelections, groupKey: string): number => {
    let distinct = 0;
    selections.get(groupKey)?.forEach((quantity) => {
        if (quantity > 0) distinct++;
    });
    return distinct;
};

// Mínimo efectivo de un grupo dentro del producto: `required` implica al menos 1
export const getGroupMinRequired = (config: { required: boolean; min: number }): number =>
    config.required ? Math.max(config.min || 0, 1) : config.min || 0;

// Máximo efectivo del renglón: fijo (config.max) o LIGADO a las piezas elegidas en otro
// renglón del producto ("un sabor por pieza": 5 Boneless → máx 5 sabores, 3 Tiras → máx 3).
// Con maxFromGroupId y nada elegido aún en el renglón ligado, el máximo es 0 (elige piezas primero).
// `dishGroups` permite resolver el renglón ligado por su CLAVE; sin él (o si la clave
// no aparece) se cae al comportamiento previo a las instancias, que ligaba por groupId.
export const getGroupEffectiveMax = (
    config: { max: number; maxFromGroupId?: string },
    selections: DishSelections,
    modifierGroupsById: Map<string, ModifierGroup>,
    dishGroups?: DishModifierGroup[]
): number => {
    if (!config.maxFromGroupId) {
        return config.max;
    }
    const rows = dishGroups || [];
    const linkedRow =
        rows.find((row) => getDishGroupKey(row) === config.maxFromGroupId) ||
        // Compat: productos guardados antes de las instancias ligaban por groupId
        rows.find((row) => row.groupId === config.maxFromGroupId);
    const linkedKey = linkedRow ? getDishGroupKey(linkedRow) : config.maxFromGroupId;
    const linkedGroup = modifierGroupsById.get(linkedRow?.groupId || config.maxFromGroupId);
    if (!linkedGroup?.id) {
        return config.max; // referencia rota: usar el máximo fijo
    }
    let pieces = 0;
    selections.get(linkedKey)?.forEach((quantity, optionId) => {
        const option = (linkedGroup.options || []).find((o) => o.id === optionId);
        pieces += quantity * (option?.quantityPerSelection || 1);
    });
    return pieces;
};

// Construye los modificadores con SNAPSHOTS DE TEXTO (nombre de grupo y de opción):
// el ticket y el tablero no dependen de hidratar el catálogo después. El nombre de
// grupo es la ETIQUETA del renglón cuando existe ("Queso — Hamburguesa 2"), así que
// el tablero de cocina distingue solo a cuál de las hamburguesas va cada elección.
export const buildOrderLineModifiers = (
    dish: Dish,
    selections: DishSelections,
    modifierGroupsById: Map<string, ModifierGroup>
): OrderLineModifier[] => {
    const modifiers: OrderLineModifier[] = [];
    (dish.modifierGroups || []).forEach((dishGroup) => {
        const group = modifierGroupsById.get(dishGroup.groupId);
        if (!group?.id) return;
        selections.get(getDishGroupKey(dishGroup))?.forEach((quantity, optionId) => {
            const option = (group.options || []).find((o) => o.id === optionId);
            if (!option || quantity <= 0) return;
            const modifier: OrderLineModifier = {
                groupId: group.id!,
                groupName: getDishGroupLabel(dishGroup, group),
                optionId: option.id,
                optionName: option.name,
                quantity,
                extraCost: option.extraCost || 0
            };
            // Solo cuando existe: Firestore rechaza undefined y los productos previos
            // a las instancias no traen instanceId
            if (dishGroup.instanceId) {
                modifier.groupInstanceId = dishGroup.instanceId;
            }
            modifiers.push(modifier);
        });
    });
    return modifiers;
};

// Rehidrata las selecciones desde una línea ya creada (editar desde el carrito).
// Compat: las líneas anteriores a las instancias solo traen groupId → se reasignan
// al primer renglón del producto que use ese grupo.
export const buildSelectionsFromModifiers = (dish: Dish, modifiers: OrderLineModifier[]): DishSelections => {
    const rows = dish.modifierGroups || [];
    const selections: DishSelections = new Map();
    (modifiers || []).forEach((modifier) => {
        const row =
            rows.find((candidate) => !!candidate.instanceId && candidate.instanceId === modifier.groupInstanceId) ||
            rows.find((candidate) => candidate.groupId === modifier.groupId);
        const key = row ? getDishGroupKey(row) : modifier.groupInstanceId || modifier.groupId;
        const options = selections.get(key) || new Map<string, number>();
        options.set(modifier.optionId, modifier.quantity);
        selections.set(key, options);
    });
    return selections;
};

// Precio unitario = precio del producto + Σ (costo extra × cantidad) de sus modificadores
export const getUnitPrice = (dishCost: number, modifiers: OrderLineModifier[]): number =>
    dishCost + modifiers.reduce((sum, modifier) => sum + modifier.extraCost * modifier.quantity, 0);

// Valida los mínimos/obligatorios de los renglones del producto.
// Devuelve el mensaje del PRIMER renglón incumplido, o undefined si todo está bien.
export const validateDishSelections = (
    dish: Dish,
    selections: DishSelections,
    modifierGroupsById: Map<string, ModifierGroup>
): string | undefined => {
    for (const dishGroup of dish.modifierGroups || []) {
        const group = modifierGroupsById.get(dishGroup.groupId);
        if (!group) continue; // referencia rota: no bloquea la venta (la UI del catálogo ya la marca)
        const groupKey = getDishGroupKey(dishGroup);
        const groupName = getDishGroupLabel(dishGroup, group);
        const minRequired = getGroupMinRequired(dishGroup);
        if (minRequired > 0 && getGroupSelectedTotal(selections, groupKey) < minRequired) {
            return minRequired === 1
                ? `Selecciona una opción de "${groupName}"`
                : `Selecciona al menos ${minRequired} opciones de "${groupName}"`;
        }
        const maxDistinct = dishGroup.maxDistinctOptions || 0;
        if (maxDistinct > 0 && getGroupDistinctCount(selections, groupKey) > maxDistinct) {
            return `Máximo ${maxDistinct} opciones distintas en "${groupName}"`;
        }
        const effectiveMax = getGroupEffectiveMax(dishGroup, selections, modifierGroupsById, dish.modifierGroups);
        if (getGroupSelectedTotal(selections, groupKey) > effectiveMax) {
            return dishGroup.maxFromGroupId
                ? `Máximo ${effectiveMax} selecciones en "${groupName}" (según las piezas elegidas)`
                : `Máximo ${effectiveMax} selecciones en "${groupName}"`;
        }
    }
    return undefined;
};

export interface BuildOrderLineParams {
    dish: Dish;
    selections: DishSelections;
    modifierGroupsById: Map<string, ModifierGroup>;
    removedIngredients: OrderLineRemovedIngredient[];
    quantity: number;
    takeAway: boolean;
    note: string;
}

// Arma la línea v2 tal cual se persistirá. `cost` = TOTAL de línea (semántica del POS
// actual) — de esto dependen subtotal, totales y los puntos del cliente.
export const buildOrderLineV2 = (params: BuildOrderLineParams): OrderLineV2 => {
    const { dish, selections, modifierGroupsById, removedIngredients, quantity, takeAway, note } = params;
    const modifiers = buildOrderLineModifiers(dish, selections, modifierGroupsById);
    const unitPrice = getUnitPrice(dish.cost, modifiers);
    return {
        product: {
            id: dish.id!,
            name: dish.name,
            cost: dish.cost,
            productCategoryId: dish.productCategoryId || ''
        },
        quantity,
        takeAway,
        note,
        modifiers,
        removedIngredients,
        unitPrice,
        cost: unitPrice * quantity,
        schemaVersion: 2
    };
};
